import React, { useMemo } from 'react';
import {
  CheckCircle2,
  XCircle,
  PauseCircle,
  Trash2,
  RotateCcw,
  Zap,
  ShieldCheck,
  Star,
  Globe,
  FileEdit,
  Lightbulb,
  Clock,
  Activity,
  Loader2,
  Copy,
} from 'lucide-react';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { useGate } from '@/client/hooks/useGate';
import { PERMISSIONS } from '@/client/config/permissions';
import { EntityFlagField } from '@/client/api/listing-management/entity.mutation';
import { truthy } from './helpers';
import type { ConfirmAction } from './types';

export const ModerationBar: React.FC<{
  entity: Record<string, any>;
  status: string;
  isDeleted: boolean;
  busy: boolean;
  setFlagPending: boolean;
  boostPending: boolean;
  undeletePending: boolean;
  onConfirm: (action: ConfirmAction) => void;
  onSuggestEdit: () => void;
}> = ({ entity: e, status, isDeleted, busy, setFlagPending, boostPending, undeletePending, onConfirm, onSuggestEdit }) => {
  // Toggling flags is an edit; the chips are native buttons so gate them inline.
  const flagsGate = useGate(PERMISSIONS.entity.edit);
  const flagChips = useMemo(
    () =>
      [
        { field: 'active' as EntityFlagField, label: 'Active', icon: Activity, on: truthy(e.active) },
        { field: 'published' as EntityFlagField, label: 'Published', icon: Globe, on: truthy(e.published) },
        { field: 'verified' as EntityFlagField, label: 'Verified', icon: ShieldCheck, on: truthy(e.verified) },
        { field: 'featured' as EntityFlagField, label: 'Featured', icon: Star, on: truthy(e.feature ?? e.featured) },
      ],
    [e]
  );

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-700">
        Moderation
        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
      </div>
      <div className="flex flex-wrap gap-2">
        <PermissionButton
          code={PERMISSIONS.entity.approve}
          size="sm"
          className="bg-emerald-600 hover:bg-emerald-700"
          disabled={busy || status === 'LIVE'}
          onClick={() => onConfirm({ kind: 'transition', status: 'LIVE', label: 'approve' })}
        >
          <CheckCircle2 className="mr-1.5 h-4 w-4" /> Approve (Live)
        </PermissionButton>
        <PermissionButton
          code={PERMISSIONS.entity.approve}
          size="sm"
          variant="outline"
          className="border-amber-300 text-amber-700 hover:bg-amber-50"
          disabled={busy || status === 'REVIEW'}
          onClick={() => onConfirm({ kind: 'transition', status: 'REVIEW', label: 'send to review' })}
        >
          <Clock className="mr-1.5 h-4 w-4" /> Review
        </PermissionButton>
        <PermissionButton
          code={PERMISSIONS.entity.approve}
          size="sm"
          variant="outline"
          className="border-orange-300 text-orange-700 hover:bg-orange-50"
          disabled={busy || status === 'SUSPENDED'}
          onClick={() => onConfirm({ kind: 'transition', status: 'SUSPENDED', label: 'suspend' })}
        >
          <PauseCircle className="mr-1.5 h-4 w-4" /> Suspend
        </PermissionButton>
        <PermissionButton
          code={PERMISSIONS.entity.approve}
          size="sm"
          variant="outline"
          className="border-rose-300 text-rose-700 hover:bg-rose-50"
          disabled={busy || status === 'REJECTED'}
          onClick={() => onConfirm({ kind: 'transition', status: 'REJECTED', label: 'reject' })}
        >
          <XCircle className="mr-1.5 h-4 w-4" /> Reject
        </PermissionButton>
        <PermissionButton
          code={PERMISSIONS.entity.approve}
          size="sm"
          variant="outline"
          disabled={busy || status === 'DRAFT'}
          onClick={() => onConfirm({ kind: 'transition', status: 'DRAFT', label: 'move to draft' })}
        >
          <FileEdit className="mr-1.5 h-4 w-4" /> Draft
        </PermissionButton>

        <div className="mx-1 w-px self-stretch bg-gray-200" />

        <PermissionButton
          code={PERMISSIONS.entity.edit}
          size="sm"
          variant="outline"
          className="border-amber-300 text-amber-700 hover:bg-amber-50"
          onClick={onSuggestEdit}
        >
          <Lightbulb className="mr-1.5 h-4 w-4" /> Suggest edit
        </PermissionButton>
        <PermissionButton code={PERMISSIONS.promotion.create} size="sm" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50" disabled={boostPending} onClick={() => onConfirm({ kind: 'boost' })}>
          <Zap className="mr-1.5 h-4 w-4" /> Boost
        </PermissionButton>
        <PermissionButton code={PERMISSIONS.entity.create} size="sm" variant="outline" disabled={busy} onClick={() => onConfirm({ kind: 'duplicate' })}>
          <Copy className="mr-1.5 h-4 w-4" /> Duplicate
        </PermissionButton>
        {isDeleted ? (
          <PermissionButton code={PERMISSIONS.entity.delete} size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" disabled={undeletePending} onClick={() => onConfirm({ kind: 'undelete' })}>
            <RotateCcw className="mr-1.5 h-4 w-4" /> Restore
          </PermissionButton>
        ) : (
          <PermissionButton code={PERMISSIONS.entity.delete} size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" disabled={busy} onClick={() => onConfirm({ kind: 'delete' })}>
            <Trash2 className="mr-1.5 h-4 w-4" /> Delete
          </PermissionButton>
        )}
      </div>

      {/* Flag toggles */}
      <div className="mt-4 flex flex-wrap gap-2 border-t border-gray-100 pt-4">
        {flagChips.map(({ field, label, icon: Icon, on }) => (
          <button
            key={field}
            disabled={setFlagPending || !flagsGate.allowed}
            onClick={() => flagsGate.allowed && onConfirm({ kind: 'flag', field, label, current: on })}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-40 ${
              on
                ? 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'
            }`}
            title={flagsGate.allowed ? `Toggle ${label}` : flagsGate.reason}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
            <span className={`ml-0.5 h-1.5 w-1.5 rounded-full ${on ? 'bg-indigo-500' : 'bg-gray-300'}`} />
          </button>
        ))}
      </div>
    </div>
  );
};
